import React, { useMemo } from "react";
import { isImageResource } from "@/types/template";
import useTemplate from "@/react/hooks/useTemplate";
import { createInstance } from "@/react/Providers/TemplateInstanceProvider";
import DrawTemplate from "@/react/components/Template/DrawTemplate";
import { adminUserImageUrlCreator, webshopUserImageUrlCreator, } from "@/react/lib/userImageUrlCreators";
// Renders one slot using the shared DrawTemplate pipeline — same renderer
// create_package2.php uses for the in-product template editor. Because
// the whole graphics layout is now HTML/CSS, DrawTemplate mounts directly
// inside the slot's div; no off-screen snapshot, no html2canvas, no
// foreignObject. Photos light up reactively as URLs load.
export function TemplateSlot({ slot, userResources, }) {
    const [template] = useTemplate(slot.defaultTemplateUuid);
    // EpmsImage's default urlCreator reads from useAssetResolver(), which
    // targets /system/xphoto/webshop. That 401s in admin context, so pick
    // the right URL builder from the URL prefix the server already chose
    // (the shipped image.url is either /admin/_xphoto or
    // /system/xphoto/webshop). The urlCreator signature receives the cell's
    // rotation, so cells sharing a photo uuid but rendered at different
    // rotations end up with distinct URLs.
    const userImageUrlCreator = useMemo(() => {
        var _a, _b;
        const firstUrl = (_a = Object.values(userResources).find((r) => { var _a; return isImageResource(r) && !!((_a = r.image) === null || _a === void 0 ? void 0 : _a.url); })) === null || _a === void 0 ? void 0 : _a.image.url;
        const isAdmin = (_b = firstUrl === null || firstUrl === void 0 ? void 0 : firstUrl.startsWith("/admin/")) !== null && _b !== void 0 ? _b : false;
        return isAdmin ? adminUserImageUrlCreator : webshopUserImageUrlCreator;
    }, [userResources]);
    if (!template) {
        return null;
    }
    const templateInstance = createInstance(template, userResources);
    const w = slot.width;
    const h = slot.height;
    // Sized to the slot's box. The parent slot wrapper handles positioning
    // and rotation; DrawTemplate just fills the slot's interior.
    return (React.createElement("div", { style: {
            position: "absolute",
            left: 0,
            top: 0,
            width: `${w}px`,
            height: `${h}px`,
        } },
        React.createElement(DrawTemplate, { templateInstance: templateInstance, containerSize: { width: w, height: h }, userImageUrlCreator: userImageUrlCreator, 
            // Package-graphics is a layout-design preview, not a quality
            // gate — the end user can't act on per-cell warnings here.
            hideQualityWarning: true })));
}
